const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const statusLabels = {
  book: "Book",
  full: "Full",
  waitlist: "Waiting list",
};

const overflowMax = 4;

export function calendarGrid(config) {
  // Read URL params for initial state
  const params = new URLSearchParams(window.location.search);
  const urlMonth = params.has("month")
    ? parseInt(params.get("month"), 10) - 1
    : null;
  const urlYear = params.has("year")
    ? parseInt(params.get("year"), 10)
    : null;
  const urlCategory = params.get("category") || null;

  let initialMonth = config.initialMonth;
  let initialYear = config.initialYear;

  if (urlMonth !== null && urlYear !== null) {
    if (
      config.months.some(
        (month) => month.index === urlMonth && month.year === urlYear,
      )
    ) {
      initialMonth = urlMonth;
      initialYear = urlYear;
    }
  } else if (urlMonth !== null) {
    // Backwards compat: month without year — find first month with that index
    const match = config.months.find((month) => month.index === urlMonth);
    if (match) {
      initialMonth = match.index;
      initialYear = match.year;
    }
  }

  const initialFilter =
    urlCategory &&
    (urlCategory === "all" ||
      config.categories.some((category) => category.slug === urlCategory))
      ? urlCategory
      : "all";

  return {
    month: initialMonth,
    year: initialYear,
    filter: initialFilter,
    months: config.months,
    categories: config.categories,
    byMonth: {},
    loading: true,
    loadError: false,
    expandedCells: new Set(),

    async init() {
      try {
        const response = await fetch(config.actionUrl);
        if (!response.ok) throw new Error(response.statusText);
        const data = await response.json();

        // Group events by year-month key then day
        const byMonth = {};
        data.events.forEach((event) => {
          const key = event.year + "-" + (event.month - 1);
          byMonth[key] = byMonth[key] || {};
          (byMonth[key][event.day] =
            byMonth[key][event.day] || []).push(event);
        });

        this.byMonth = byMonth;

        // Filter categories to only those with events
        const activeCats = new Set(data.events.map((e) => e.cat));
        this.categories = this.categories.filter((c) => activeCats.has(c.slug));
        if (this.filter !== "all" && !activeCats.has(this.filter)) {
          this.filter = "all";
        }

        // Refine months to only those with events
        if (data.months.length) {
          this.months = data.months;

          // Re-apply month if current selection has no events
          if (
            !this.months.some(
              (month) =>
                month.index === this.month && month.year === this.year,
            )
          ) {
            this.month = data.initialMonth;
            this.year = data.initialYear;
          }
        }
      } catch {
        this.loadError = true;
      } finally {
        this.loading = false;
        this.scrollActiveFilterIntoView();
      }
    },

    scrollActiveFilterIntoView() {
      if (this.filter === "all") return;

      this.$nextTick(() => {
        const row = this.$refs.filterRow;
        if (!row) return;

        const activeChip = row.querySelector(".cal-filter-chip.active");
        if (!activeChip) return;

        const rowRect = row.getBoundingClientRect();
        const chipRect = activeChip.getBoundingClientRect();
        const scrollLeft =
          row.scrollLeft +
          chipRect.left -
          rowRect.left -
          rowRect.width / 2 +
          chipRect.width / 2;

        row.scrollTo({ left: scrollLeft, behavior: "smooth" });
      });
    },

    get monthKey() {
      return this.year + "-" + this.month;
    },

    get gridViewUrl() {
      let base = config.gridUrl;
      if (this.filter !== "all") {
        const cat = this.categories.find((c) => c.slug === this.filter);
        if (cat?.url) {
          base = cat.url;
        }
      }
      const params = new URLSearchParams();
      params.set("month", this.month + 1);
      params.set("year", this.year);
      return base + "?" + params.toString();
    },

    pushUrl() {
      const searchParams = new URLSearchParams();
      searchParams.set("month", this.month + 1);
      searchParams.set("year", this.year);
      if (this.filter !== "all") {
        searchParams.set("category", this.filter);
      }
      const url =
        window.location.pathname + "?" + searchParams.toString();
      window.history.replaceState(null, "", url);

      this.scrollToNav();
    },

    scrollToNav() {
      const nav = this.$refs.monthNav;
      if (!nav) return;

      if (window.APP?.hideStickyHeader) {
        window.APP.hideStickyHeader();
      }

      nav.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    get monthLabel() {
      return monthNames[this.month];
    },

    statusLabel(status) {
      return statusLabels[status] || status;
    },

    findCurrentMonthIndex() {
      return this.months.findIndex(
        (month) => month.index === this.month && month.year === this.year,
      );
    },

    canGoPrev() {
      return this.findCurrentMonthIndex() > 0;
    },

    canGoNext() {
      const index = this.findCurrentMonthIndex();
      return index >= 0 && index < this.months.length - 1;
    },

    prevMonth() {
      const index = this.findCurrentMonthIndex();
      if (index > 0) {
        const prev = this.months[index - 1];
        this.month = prev.index;
        this.year = prev.year;
        this.pushUrl();
      }
    },

    nextMonth() {
      const index = this.findCurrentMonthIndex();
      if (index >= 0 && index < this.months.length - 1) {
        const next = this.months[index + 1];
        this.month = next.index;
        this.year = next.year;
        this.pushUrl();
      }
    },

    setMonth(index, year) {
      this.month = index;
      this.year = year;
      this.expandedCells.clear();
      this.pushUrl();
    },

    setFilter(category) {
      this.filter = category;
      this.expandedCells.clear();
      this.pushUrl();
    },

    get cells() {
      const currentMonth = this.month;
      const firstDow = new Date(this.year, currentMonth, 1).getDay();
      const daysInMonth = new Date(
        this.year,
        currentMonth + 1,
        0,
      ).getDate();
      const prevMonthDays = new Date(this.year, currentMonth, 0).getDate();
      const monthEvents = this.byMonth[this.monthKey] || {};

      const today = new Date();
      const todayDay = today.getDate();
      const isCurrentMonth =
        this.year === today.getFullYear() &&
        this.month === today.getMonth();

      const cells = [];

      // Previous month padding
      for (let i = firstDow - 1; i >= 0; i--) {
        cells.push({ num: prevMonthDays - i, isOut: true, isPast: false, events: [] });
      }

      // Current month days
      for (let day = 1; day <= daysInMonth; day++) {
        let events = monthEvents[day] || [];
        if (this.filter !== "all") {
          events = events.filter(
            (event) => event.cat === this.filter,
          );
        }
        const isPast = isCurrentMonth && day < todayDay;
        const isToday = isCurrentMonth && day === todayDay;
        cells.push({ num: day, isOut: false, isPast, isToday, events });
      }

      // Next month padding
      let nextDay = 1;
      while (cells.length % 7 !== 0) {
        cells.push({ num: nextDay++, isOut: true, events: [] });
      }

      return cells;
    },

    cellClass(cell, index) {
      const classes = ["cal-cell"];
      if (cell.isOut) classes.push("is-out");
      if (cell.isPast) classes.push("is-past");
      const col = index % 7;
      if (col === 0 || col === 6) classes.push("is-weekend");
      return classes.join(" ");
    },

    chipClass(event) {
      let className = "cal-chip";
      if (event.past) className += " past";
      else if (event.status === "full" || event.status === "waitlist") className += " full";
      else className += " cat-" + event.colorLabel;
      return className;
    },

    visibleEvents(cell) {
      if (this.expandedCells.has(cell.num)) return cell.events;
      return cell.events.slice(0, overflowMax);
    },

    overflowCount(cell) {
      if (this.expandedCells.has(cell.num)) return 0;
      return Math.max(0, cell.events.length - overflowMax);
    },

    toggleExpand(cell) {
      if (this.expandedCells.has(cell.num)) {
        this.expandedCells.delete(cell.num);
      } else {
        this.expandedCells.add(cell.num);
      }
    },

    dayName(day) {
      const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return daysOfWeek[new Date(this.year, this.month, day).getDay()];
    },

    get eventDays() {
      const monthEvents = this.byMonth[this.monthKey] || {};
      const today = new Date();
      const todayDay = today.getDate();
      const isCurrentMonth =
        this.year === today.getFullYear() &&
        this.month === today.getMonth();
      const days = [];

      for (const day of Object.keys(monthEvents)
        .map(Number)
        .sort((a, b) => a - b)) {
        let events = monthEvents[day];
        if (this.filter !== "all") {
          events = events.filter(
            (event) => event.cat === this.filter,
          );
        }
        if (events.length > 0) {
          const isPast = isCurrentMonth && day < todayDay;
          days.push({ num: day, isPast, events });
        }
      }

      return days;
    },

    get hasEvents() {
      const monthEvents = this.byMonth[this.monthKey] || {};
      for (const day in monthEvents) {
        let events = monthEvents[day];
        if (this.filter !== "all") {
          events = events.filter(
            (event) => event.cat === this.filter,
          );
        }
        if (events.length > 0) return true;
      }
      return false;
    },
  };
}
